/**
 * Search and bookmark hits open the message they came from.
 * /chat?session= does not match the chat route, and /bookmarks
 * is the list, not the message.
 * A room thread opens in that room, not on the Idea page.
 */
import { rememberStoryId } from './lastProject';
import { plottrackPath } from './plottrackPath';
import { PANE_ROOM_KEYS, rememberRoomSessionId } from './roomChat';
import { bookNotesPath } from './seriesNotes';

export function messageAnchorId(searchValue, hash) {
  const fromQuery = typeof searchValue === 'string'
    ? new URLSearchParams(searchValue).get('message')
    : (searchValue?.get?.('message') || '');
  if (fromQuery) return fromQuery;
  const raw = String(hash || '').replace(/^#/, '');
  if (!raw) return '';
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function chatMessagePath(sessionId, messageId) {
  if (!sessionId || !messageId) return null;
  return `/chat/${sessionId}#${messageId}`;
}

function messageHash(messageId) {
  return messageId ? `#${encodeURIComponent(messageId)}` : '';
}

/**
 * Idea stays on /chat. Outline, Write, Notes, and Docs open the room
 * the message was sent in, with the hash still naming the message.
 */
export function roomMessagePath({
  roomKey,
  storyId,
  projectId,
  sessionId,
  messageId,
} = {}) {
  const hash = messageHash(messageId);
  const room = String(roomKey || '').toLowerCase();
  if (room === 'outline' && storyId) return `${plottrackPath(storyId)}${hash}`;
  if (room === 'write' && storyId) return `/scene-writing/${storyId}${hash}`;
  if (room === 'notes' && storyId) return `/bibles${hash}`;
  if (room === 'docs' && (projectId || storyId)) {
    return `${bookNotesPath(projectId, storyId)}${hash}`;
  }
  return chatMessagePath(sessionId, messageId);
}

/** The room pane reads this id the next time that room opens. */
export function rememberRoomMessage({ roomKey, storyId, sessionId } = {}) {
  const room = String(roomKey || '').toLowerCase();
  if (!PANE_ROOM_KEYS.includes(room) || !storyId || !sessionId) return;
  if (room === 'notes') rememberStoryId(storyId);
  rememberRoomSessionId(storyId, room, sessionId);
}

export function bookmarkRowMessage(rows, bookmarkId) {
  const list = Array.isArray(rows) ? rows : (rows?.data || []);
  const row = list.find((item) => item && String(item.id) === String(bookmarkId));
  if (!row || row.is_synthesis || !row.message_id) return null;
  return {
    messageId: row.message_id,
    sessionId: row.session_id || null,
  };
}

export function sessionIdFromMessageDetails(payload) {
  const body = payload?.data && payload.data.session_id ? payload.data : payload;
  return body?.session_id || null;
}

export function directMessageTarget(result, type) {
  if (!result || (type !== 'message' && type !== 'summary' && type !== 'bookmark')) {
    return null;
  }
  if (type === 'bookmark' && result.metadata?.is_synthesis) return null;
  const sessionId = result.metadata?.session_id || null;
  const messageId = type === 'bookmark'
    ? (result.metadata?.message_id || result.message_id || null)
    : (result.id || null);
  if (!sessionId || !messageId) return null;
  return { sessionId, messageId, path: chatMessagePath(sessionId, messageId) };
}
